/**
 * Benchmarks Page — Peer Comparison & Percentile Visualization
 *
 * Shows available benchmark metrics with percentile distribution bars.
 * For investee_reporter role: shows their position on the percentile bars.
 *
 * Data from:
 *   GET /benchmarks/available — list of metrics with p25/p50/p75
 *   Investee position included when user role is investee_reporter.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart2,
  TrendingUp,
  Loader2,
  ArrowUp,
  ArrowDown,
  Minus,
} from 'lucide-react';
import { clsx } from 'clsx';
import { apiClient } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';

// ─── Types ──────────────────────────────────────────────────────

interface BenchmarkMetric {
  id: string;
  name: string;
  category: string;
  unit: string;
  p25: number;
  p50: number;
  p75: number;
  min: number;
  max: number;
  your_value: number | null;
  your_percentile: number | null;
  sample_size: number;
}

// ─── Data Fetching ──────────────────────────────────────────────

function useBenchmarks() {
  return useQuery<BenchmarkMetric[]>({
    queryKey: ['ic', 'benchmarks', 'available'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/benchmarks/available');
      return res.data?.data ?? res.data ?? [];
    },
    staleTime: 300_000,
  });
}

// ─── Main Component ─────────────────────────────────────────────

export function BenchmarksPage() {
  const { icRole } = useAuth();
  const isInvestee = icRole === 'investee_reporter';
  const benchmarksQuery = useBenchmarks();
  const metrics = benchmarksQuery.data ?? [];

  const grouped = useMemo(() => {
    const groups: Record<string, BenchmarkMetric[]> = {};
    for (const metric of metrics) {
      const cat = metric.category || 'General';
      if (!groups[cat]) {
        groups[cat] = [];
      }
      groups[cat]!.push(metric);
    }
    return groups;
  }, [metrics]);

  const categories = Object.keys(grouped).sort();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-lg font-semibold text-ic-text-primary">Benchmarks</h1>
        <p className="mt-0.5 text-xs text-ic-text-tertiary">
          {isInvestee
            ? 'Compare your performance against peer benchmarks'
            : 'Peer benchmark distributions across ESG and operational metrics'}
        </p>
      </div>

      {/* Summary */}
      {!benchmarksQuery.isLoading && metrics.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <SummaryCard
            label="Metrics Available"
            value={metrics.length.toString()}
            icon={<BarChart2 className="h-4 w-4" />}
          />
          <SummaryCard
            label="Categories"
            value={categories.length.toString()}
            icon={<TrendingUp className="h-4 w-4" />}
          />
          {isInvestee && (
            <SummaryCard
              label="Your Rankings"
              value={metrics.filter((m) => m.your_percentile !== null).length.toString()}
              icon={<BarChart2 className="h-4 w-4" />}
            />
          )}
        </div>
      )}

      {/* Loading */}
      {benchmarksQuery.isLoading && (
        <div className="flex h-48 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-ic-text-tertiary" />
        </div>
      )}

      {/* Empty */}
      {!benchmarksQuery.isLoading && metrics.length === 0 && (
        <div className="flex h-48 items-center justify-center rounded-lg border border-dashed border-ic-border">
          <p className="text-xs text-ic-text-tertiary">
            No benchmark data available yet
          </p>
        </div>
      )}

      {/* Grouped Metrics */}
      {categories.map((category) => {
        const categoryMetrics = grouped[category]!;
        return (
          <section key={category}>
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">
              {category}
            </h2>

            {/* Percentile Bars */}
            <div className="space-y-3">
              {categoryMetrics.map((metric) => (
                <PercentileBar key={metric.id} metric={metric} showUser={isInvestee} />
              ))}
            </div>

            {/* Data Table */}
            <div className="mt-4 rounded-lg border border-ic-border bg-ic-card overflow-x-auto">
              <table className="w-full text-xs ic-table">
                <thead>
                  <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
                    <th className="py-2.5 px-4 font-medium">Metric</th>
                    {isInvestee && <th className="py-2.5 px-3 font-medium text-right">Your Value</th>}
                    <th className="py-2.5 px-3 font-medium text-right">P25</th>
                    <th className="py-2.5 px-3 font-medium text-right">Median</th>
                    <th className="py-2.5 px-3 font-medium text-right">P75</th>
                    {isInvestee && <th className="py-2.5 px-3 font-medium text-right">Your Percentile</th>}
                    <th className="py-2.5 px-3 pr-4 font-medium text-right">Sample</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryMetrics.map((metric) => (
                    <MetricRow key={metric.id} metric={metric} showUser={isInvestee} />
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
}

// ─── Summary Card ───────────────────────────────────────────────

interface SummaryCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
}

function SummaryCard({ label, value, icon }: SummaryCardProps) {
  return (
    <div className="rounded-lg border border-ic-border bg-ic-card p-4">
      <div className="flex items-center gap-2 text-ic-text-tertiary">
        {icon}
        <span className="text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <p className="mt-2 text-xl font-semibold text-ic-text-primary tracking-tight">{value}</p>
    </div>
  );
}

// ─── Percentile Bar ─────────────────────────────────────────────

interface PercentileBarProps {
  metric: BenchmarkMetric;
  showUser: boolean;
}

function PercentileBar({ metric, showUser }: PercentileBarProps) {
  const min = typeof metric.min === 'number' ? metric.min : 0;
  const max = typeof metric.max === 'number' ? metric.max : 0;
  const range = max - min;
  if (range <= 0) return null;

  const p25 = typeof metric.p25 === 'number' ? metric.p25 : 0;
  const p50 = typeof metric.p50 === 'number' ? metric.p50 : 0;
  const p75 = typeof metric.p75 === 'number' ? metric.p75 : 0;
  const p25Pos = ((p25 - min) / range) * 100;
  const p50Pos = ((p50 - min) / range) * 100;
  const p75Pos = ((p75 - min) / range) * 100;

  const userPos =
    showUser && typeof metric.your_value === 'number'
      ? ((metric.your_value - min) / range) * 100
      : null;

  return (
    <div className="rounded-lg border border-ic-border bg-ic-card p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-xs font-medium text-ic-text-primary">{metric.name}</h4>
          <span className="text-[10px] text-ic-text-tertiary">{metric.unit}</span>
        </div>
        {showUser && metric.your_percentile !== null && (
          <PercentileRank percentile={metric.your_percentile} />
        )}
      </div>

      {/* Bar */}
      <div className="relative h-6 w-full rounded-full bg-slate-700/50">
        {/* Interquartile range (P25-P75) */}
        <div
          className="absolute top-0 h-full rounded-full bg-ic-accent/20"
          style={{
            left: `${Math.max(0, p25Pos)}%`,
            width: `${Math.max(0, p75Pos - p25Pos)}%`,
          }}
        />

        {/* P25 marker */}
        <PercentileMarker position={p25Pos} label="P25" color="text-slate-400" />

        {/* P50 (median) marker */}
        <PercentileMarker position={p50Pos} label="P50" color="text-ic-accent" isBold />

        {/* P75 marker */}
        <PercentileMarker position={p75Pos} label="P75" color="text-slate-400" />

        {/* User position */}
        {userPos !== null && (
          <div
            className="absolute top-0 h-full w-0.5 bg-emerald-400"
            style={{ left: `${Math.max(0, Math.min(100, userPos))}%` }}
          >
            <div className="absolute -top-5 left-1/2 -translate-x-1/2 rounded bg-emerald-400 px-1 py-0.5 text-[8px] font-bold text-white whitespace-nowrap">
              You
            </div>
          </div>
        )}
      </div>

      {/* Scale labels */}
      <div className="mt-1 flex justify-between text-[9px] text-ic-text-tertiary">
        <span>{formatMetricValue(min, metric.unit)}</span>
        <span>{formatMetricValue(max, metric.unit)}</span>
      </div>
    </div>
  );
}

// ─── Percentile Marker ──────────────────────────────────────────

interface MarkerProps {
  position: number;
  label: string;
  color: string;
  isBold?: boolean;
}

function PercentileMarker({ position, label, color, isBold }: MarkerProps) {
  return (
    <div
      className="absolute top-0 h-full"
      style={{ left: `${Math.max(0, Math.min(100, position))}%` }}
    >
      <div
        className={clsx(
          'h-full w-px',
          isBold ? 'bg-ic-accent' : 'bg-slate-500',
        )}
      />
      <span
        className={clsx(
          'absolute -bottom-4 left-1/2 -translate-x-1/2 text-[8px]',
          color,
          isBold && 'font-semibold',
        )}
      >
        {label}
      </span>
    </div>
  );
}

// ─── Percentile Rank Badge ──────────────────────────────────────

function PercentileRank({ percentile }: { percentile: number }) {
  const safePercentile = typeof percentile === 'number' ? percentile : 0;
  const tier = safePercentile >= 75 ? 'top' : safePercentile >= 50 ? 'mid' : 'low';

  const colors = {
    top: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
    mid: 'bg-amber-500/15 text-amber-400 border-amber-500/25',
    low: 'bg-red-500/15 text-red-400 border-red-500/25',
  };

  const icons = {
    top: <ArrowUp className="h-3 w-3" />,
    mid: <Minus className="h-3 w-3" />,
    low: <ArrowDown className="h-3 w-3" />,
  };

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium',
        colors[tier],
      )}
    >
      {icons[tier]}
      P{Math.round(safePercentile)}
    </span>
  );
}

// ─── Metric Table Row ───────────────────────────────────────────

interface MetricRowProps {
  metric: BenchmarkMetric;
  showUser: boolean;
}

function MetricRow({ metric, showUser }: MetricRowProps) {
  return (
    <tr className="border-b border-ic-border/50 last:border-0">
      <td className="py-2.5 px-4 text-xs font-medium text-ic-text-primary">
        {metric.name}
      </td>
      {showUser && (
        <td className="py-2.5 px-3 text-right font-mono text-xs text-ic-text-secondary">
          {metric.your_value !== null
            ? formatMetricValue(metric.your_value, metric.unit)
            : '--'}
        </td>
      )}
      <td className="py-2.5 px-3 text-right font-mono text-xs text-ic-text-tertiary">
        {formatMetricValue(metric.p25, metric.unit)}
      </td>
      <td className="py-2.5 px-3 text-right font-mono text-xs text-ic-text-secondary">
        {formatMetricValue(metric.p50, metric.unit)}
      </td>
      <td className="py-2.5 px-3 text-right font-mono text-xs text-ic-text-tertiary">
        {formatMetricValue(metric.p75, metric.unit)}
      </td>
      {showUser && (
        <td className="py-2.5 px-3 text-right">
          {metric.your_percentile !== null ? (
            <PercentileRank percentile={metric.your_percentile} />
          ) : (
            <span className="text-xs text-ic-text-tertiary">--</span>
          )}
        </td>
      )}
      <td className="py-2.5 px-3 pr-4 text-right font-mono text-[10px] text-ic-text-tertiary">
        n={metric.sample_size}
      </td>
    </tr>
  );
}

// ─── Formatters ─────────────────────────────────────────────────

function formatMetricValue(value: number | null | undefined, unit: string): string {
  if (typeof value !== 'number' || isNaN(value)) return '--';

  const lowerUnit = (unit ?? '').toLowerCase();

  if (lowerUnit === '%' || lowerUnit === 'percent') {
    return `${value.toFixed(1)}%`;
  }

  if (lowerUnit === 'eur' || lowerUnit === 'usd') {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
    return value.toLocaleString();
  }

  if (Number.isInteger(value)) {
    return value.toLocaleString();
  }

  return value.toFixed(2);
}
